import { getCookieStorage } from '@shared/core/storage';

/**
 * The acting guest's session as a per-operation Apollo `context`, or `undefined` when there is none.
 *
 * The gateway authorizes guests from the `x-joy-sessionid` header. The `joy_session_id` cookie carries
 * that session, but only reaches the gateway when the page is same-origin with it — on a masked or
 * custom domain, inside an embedded iframe, or in local development against a remote gateway it is
 * never sent, so any session-authorized guest operation has to forward it by hand.
 *
 * **Deliberately a function, not a captured value.** A guest's session can be minted *during* a
 * submit (`saveEventRegistration` creates the contact and returns the key), so anything that reads
 * the cookie once — at mount, or even during a render — can be stale by the time the request goes
 * out. Calling this at the moment of each request is what makes that class of bug impossible rather
 * than merely absent today.
 *
 * Returns `undefined` rather than an empty header when there is no session: the server must see a
 * missing session and refuse or degrade, not an empty one it might treat differently.
 *
 * Lives here rather than beside the cookie it reads because `getCookieStorage` is defined *in* the
 * storage barrel, so a sibling module importing it forms a barrel cycle (a real TDZ risk — that
 * barrel initializes local storage as a side effect on import).
 */
export const sessionHeaderContext = (): { headers: { 'x-joy-sessionid': string } } | undefined => {
  const sessionId = getCookieStorage()?.getItem('_session_id') ?? undefined;
  return sessionId ? { headers: { 'x-joy-sessionid': sessionId } } : undefined;
};
